import React, { useContext, useEffect, useState, useReducer, useMemo, ChangeEvent } from "react";
import { AuthContext } from "../contexts/AuthContext";

import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { usePage } from "../../routes";
import { HvSubstations } from "../../pages/HvSubstations";
import { WorkorderViewer } from "../workorders/WorkorderViewer";
import { useHvSubstationById } from "../data/Assets/useHvSubstationById";
import { useAddressById } from "../data/useAddressById";
import { useLocationById } from "../data/useLocationById";
import { useHVSubstationsByCustomer } from "../data/Assets/useHVSubstationsByCustomer";
import { useHvSubstationUpdate } from "../data/Assets/useHvSubstationUpdate";
import { useHvSubstationDelete } from "../data/Assets/useHvSubstationDelete";
import { Controller, useForm } from "react-hook-form";

import { Col, Container, Row } from "react-bootstrap";
import { Button, Box, Alert } from "@mui/material";

import { Card } from "../shared/Card";
import { FormContainer, FormItem } from "../shared/FormLayout/FormContainer";
import { MessageToast, ToastMode } from "../shared/MessageToast";
import { ProgressButton } from "../shared/ProgressButton";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { InputText } from "primereact/inputtext";
import { Snackbar } from "../shared/Snackbar";
import { MessageCard, MessageType } from "../shared/MessageCard";
import RouteBreadcrumbs from "../shared/RouteBreadcrumbs";
import DeleteConfirmationButton from "../shared/FormLayout/Buttons/DeleteConfirmationButton";

import { Address } from "../shared/interfaces/Address";
import { HvSubstation, HvSubstationSubmission } from "../shared/interfaces/HvSubstation";
import { HvSubstationErrorResponse } from "../shared/interfaces/HvSubstation";
import { HVSubstationSetupErrorCode } from "../shared/enums/ErrorCodes";
import { convertAssetToHvSubstation } from "../shared/helpers/HvSubstationHelpers";
import { UpdatableSwitchgearDetails } from "./UpdatableSwitchgearDetails";

import "./coordinateInput.css";
import { ValidationMessage } from "../shared/ValidationMessage";
import { CancelButton } from "../shared/CancelButton";
import { CompanyDropdown } from "../CompanyDropdown";
import { SiteDropdown } from "../SiteDropdown";
import { useCompaniesByCustomerId } from "../data/useCompaniesByCustomerId";
import { useAlternativeRegionNames } from "../data/Settings/useAlternativeRegionNames";
import { InputTextarea } from "primereact/inputtextarea";
import { AccessControlContext } from "../contexts/AccessControlContext";

export function HvSubstationViewer() {
  const { t } = useTranslation();

  //get the substation's asset ID from the URL
  const { assetId } = useParams<{ assetId?: string }>();
  const hvSubstationPagePath = usePage(HvSubstations.name)?.path;
  const workorderViewerPath = usePage(WorkorderViewer.name)?.path;
  const navigate = useNavigate();

  const retrievedAsset = useHvSubstationById(assetId);
  const customersHvSubstations = useHVSubstationsByCustomer(retrievedAsset.data?.customerId);
  const retrievedLocation = useLocationById(retrievedAsset.data?.locationId);
  const retrievedAddress = useAddressById(retrievedLocation.data?.addressId);

  const altRegionNames = useAlternativeRegionNames(retrievedAsset.data?.customerId);

  const companies = useCompaniesByCustomerId(retrievedAsset.data?.customerId);

  const {
    mutate: updateHvSubstation,
    isError: updateHvSubstationError,
    isLoading: updateHvSubstationLoading,
    reset: resetUpdateHvSubstation,
  } = useHvSubstationUpdate({ onSuccess: onUpdateSubstationSuccess });

  const hvSubstationDelete = useHvSubstationDelete({
    onSuccess: onDeleteHvSubstationSuccess,
    onError: onDeleteHvSubstationError,
  });

  const {
    register,
    formState: { errors },
    clearErrors,
    reset,
    trigger,
    setValue,
    control,
  } = useForm<HvSubstation>({ mode: "onBlur" });

  const [hvSubstation, setHvSubstation] = useReducer(
    (hvSubstation: Partial<HvSubstation> | undefined, updatedHvSubstation: Partial<HvSubstation> | undefined) => {
      return { ...hvSubstation, ...updatedHvSubstation };
    },
    {},
  );

  const [address, setAddress] = useReducer(
    (address: Partial<Address> | undefined, updatedAddress: Partial<Address> | undefined) => {
      return { ...address, ...updatedAddress };
    },
    {},
  );

  const [companyName, setCompanyName] = useState<string | undefined>(undefined);

  const [siteName, setSiteName] = useState<string | undefined>(undefined);

  const [editingHvSubstation, setEditingHvSubstation] = useState(false);

  const companyId = useMemo(() => {
    if (companies && companyName) {
      return companies.data?.find((c) => c.name === companyName)?.id;
    }
  }, [companies, companyName]);

  const authContext = useContext(AuthContext);
  const userCanViewSubstation = authContext.canI("view", "customer", retrievedAsset.data?.customerId);
  const userCanEditHvSubstationAtCustomerLevel = authContext.canI(
    "edit",
    "hvSubstations",
    retrievedAsset.data?.customerId,
  );

  const accessControlContext = useContext(AccessControlContext);
  const userCanEditHvSubstation =
    hvSubstation && accessControlContext.canI("edit", "hvSubstations", retrievedAsset.data?.customerId);
  const userCanDeleteHvSubstation =
    hvSubstation && accessControlContext.canI("delete", "hvSubstations", retrievedAsset.data?.customerId);

  const [pageLoading, setPageLoading] = useState(retrievedAsset.isLoading);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");

  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorSnackbarText, setErrorSnackbarText] = useState<string>("");

  const [affectedWorkOrderIds, setAffectedWorkOrderIds] = useState<Number[]>([]);

  useEffect(() => {
    setPageLoading(
      retrievedAsset.isLoading ||
        customersHvSubstations.isLoading ||
        retrievedLocation.isLoading ||
        retrievedAddress.isLoading,
    );
  }, [
    customersHvSubstations.isLoading,
    retrievedAddress.isLoading,
    retrievedAsset.isLoading,
    retrievedLocation.isLoading,
  ]);

  useEffect(() => {
    if (retrievedAsset.data) {
      setHvSubstation(convertAssetToHvSubstation(retrievedAsset.data));
    }
  }, [retrievedAsset.data]);

  useEffect(() => {
    if (retrievedAddress.data) {
      setAddress(retrievedAddress.data);
    }
  }, [retrievedAddress.data]);

  useEffect(() => {
    if (hvSubstation.company) {
      setCompanyName(hvSubstation.company);
    }
  }, [hvSubstation.company]);

  useEffect(() => {
    if (hvSubstation.site) {
      setSiteName(hvSubstation.site);
    }
  }, [hvSubstation.site]);

  function onUpdateSubstationSuccess() {
    setSnackbarText(t("common.dataUpdated", { val: hvSubstation.name }));
    setShowSnackbar(true);

    setEditingHvSubstation(false);
    resetUpdateHvSubstation();
    retrievedAsset.refetch();
  }

  function deleteHvSubstation() {
    userCanDeleteHvSubstation && retrievedAsset.data && hvSubstationDelete.mutate(retrievedAsset.data.id);
  }

  function onDeleteHvSubstationSuccess() {
    hvSubstationPagePath &&
      navigate(hvSubstationPagePath, {
        state: { deletedHvSubstationName: retrievedAsset.data?.name },
      });
  }

  function onDeleteHvSubstationError(errorResponse: Response) {
    if (errorResponse.status === 403) {
      setErrorSnackbarText(
        t("common.operationForbiddenError", {
          operation: t("common.delete"),
          item: t("substations.substation"),
        }),
      );
      setShowErrorSnackbar(true);
    } else if (errorResponse.status === 409) {
      errorResponse.json().then((error: HvSubstationErrorResponse) => {
        if (error.errorCode === HVSubstationSetupErrorCode.ActiveWorkOrderExistsOnHVSubstationException) {
          setErrorSnackbarText(t("substations.unableToDeleteSubstationError"));
          setAffectedWorkOrderIds(error.affectedIds);
          setShowErrorSnackbar(true);
        } else if (error.errorCode === HVSubstationSetupErrorCode.HVSubstationDoesNotExistException) {
          setErrorSnackbarText(t("substations.substationNotFoundError"));
          setShowErrorSnackbar(true);
        }
      });
    } else {
      setErrorSnackbarText(t("substations.substationDeleteGenericError"));
      setShowErrorSnackbar(true);
    }
  }

  function handleInputChange(event: any) {
    const fieldName = event.target.name as keyof HvSubstation;
    const value = event.target.value;

    clearErrors(fieldName);
    setValue(fieldName, value, { shouldValidate: false });
    setHvSubstation({ ...hvSubstation, [fieldName]: value });
  }

  const staticAddress = (
    <React.Fragment>
      <div>
        <div>{address.name}</div>
        <div>{address.line1}</div>
        <div>{address.line2}</div>
        <div>{address.line3}</div>
        <div>{address.line4}</div>
        <div>{address.locality}</div>
        <div>{address.region}</div>
        <div>{address.postCode}</div>
        <div>{address.country}</div>
      </div>
    </React.Fragment>
  );

  function addressInputTemplate(key: keyof Address, maxLength?: number, placeholder?: string) {
    return (
      <span className="p-float-label w-100" style={{ marginTop: "25px" }}>
        <InputText
          key={`input-${key}`}
          name={key}
          value={address[key]?.toString() || ""}
          onChange={handleAddressInputChange}
          maxLength={maxLength}
        />
        <label htmlFor="inputtext">{placeholder}</label>
      </span>
    );
  }

  function handleAddressInputChange(event: any) {
    const name = event.target.name as keyof Address;
    const value = event.target.value;

    setAddress({ ...address, [name]: value });
  }

  const editableSubstationAddress = (
    <React.Fragment>
      <div>
        {addressInputTemplate("name", 100, t("common.name", { entity: "$t(address.address)" }))}
        {addressInputTemplate("line1", 100, t("address.line1"))}
        {addressInputTemplate("line2", 100, t("address.line2"))}
        {addressInputTemplate("line3", 100, t("address.line3"))}
        {addressInputTemplate("line4", 100, t("address.line4"))}
        {addressInputTemplate("locality", 100, t("address.locality"))}
        {addressInputTemplate("region", 100, t("address.region"))}
        {addressInputTemplate("postCode", 50, t("address.postCode"))}
        {addressInputTemplate("country", 100, t("address.country"))}
      </div>
    </React.Fragment>
  );

  function coordinateInputTemplateWithValidation(key: keyof HvSubstation, minValue: number, maxValue: number) {
    if (key === "latitude" || key === "longitude") {
      return (
        <div>
          <div>
            <InputText
              type="number"
              key={`input-${key}`}
              value={hvSubstation[key]}
              className={errors[key] ? "p-invalid" : undefined}
              id={key}
              {...register(key, { max: maxValue, min: minValue })}
              onChange={handleInputChange}
              name={key}
            />
          </div>
          <div>
            {errors[key] && (errors[key]?.type === "max" || errors[key]?.type === "min") && (
              <ValidationMessage
                invalidMessage={t("common.minMaxNumberValidationMessage", {
                  min: minValue,
                  max: maxValue,
                })}
                validationType="error"
                showIcon
              />
            )}
          </div>
        </div>
      );
    }
  }

  function handleCompanyChange(companyName: string | undefined) {
    setCompanyName(companyName);
    setSiteName(undefined);

    // If the user does not have admin or customer level permissions but do have company or
    // site level permissions, add validation to the company and site drop downs to ensure
    // they select a value, as they can only create resources at company/site level.
    setValue("company", companyName ?? "", {
      shouldValidate: !userCanEditHvSubstationAtCustomerLevel,
    });

    setValue("site", undefined, {
      shouldValidate: !userCanEditHvSubstationAtCustomerLevel,
    });

    if (!userCanEditHvSubstationAtCustomerLevel) {
      clearErrors(["company", "site"]);
    }
  }

  function handleSiteChange(siteName: string | undefined) {
    setSiteName(siteName);

    // If the user does not have admin or customer level permissions but do have company
    // or site level permissions, add validation to the site drop down to ensure they
    // select a value, as they can only create resources at company/site level.
    setValue("site", siteName ?? "", {
      shouldValidate: !userCanEditHvSubstationAtCustomerLevel,
    });

    if (!userCanEditHvSubstationAtCustomerLevel) {
      clearErrors("site");
    }
  }

  function handleAccessNotesChange(event: ChangeEvent<HTMLTextAreaElement>) {
    const accessNotes = event.target.value;
    setHvSubstation({ ...hvSubstation, accessNotes: accessNotes });
    setValue("accessNotes", accessNotes, { shouldValidate: true });
    clearErrors("accessNotes");
  }

  const hvSubstationDetailsList: FormItem[] = [
    {
      label: t("substations.substationName"),
      required: editingHvSubstation && true,
      value: editingHvSubstation ? (
        <div>
          <div>
            <InputText
              value={hvSubstation.name?.toString() || ""}
              maxLength={50}
              className={errors.name ? "p-invalid" : undefined}
              id={"name"}
              {...register("name", {
                required: t<string>("common.pleaseEnter", {
                  val: "$t(substations.substationName)",
                }),
                value: hvSubstation.name,
              })}
              onChange={handleInputChange}
              name={"name"}
            />
          </div>
          <div>
            {errors?.name?.message && (
              <ValidationMessage invalidMessage={errors?.name?.message} validationType="error" showIcon />
            )}
          </div>
        </div>
      ) : (
        hvSubstation?.name
      ),
    },
    {
      label: t("substations.substationNumber"),
      required: editingHvSubstation && true,
      value: editingHvSubstation ? (
        <div>
          <div>
            <InputText
              value={hvSubstation.code?.toString() || ""}
              maxLength={50}
              className={errors.code ? "p-invalid" : undefined}
              id={"code"}
              {...register("code", {
                required: t<string>("common.pleaseEnter", {
                  val: "$t(substations.substationNumber)",
                }),
                value: hvSubstation.code,
                validate: {
                  notDuplicate: (value) =>
                    !customersHvSubstations.data?.some(
                      (substation) => substation.serialNumber === value && substation.id !== hvSubstation.id,
                    ),
                },
              })}
              onChange={handleInputChange}
              name={"code"}
            />
          </div>
          <div>
            {errors?.code?.message && (
              <ValidationMessage invalidMessage={errors?.code?.message} validationType="error" showIcon />
            )}
            {errors?.code?.type === "notDuplicate" && (
              <ValidationMessage
                invalidMessage={t("substations.substationNumberDuplicationError")}
                validationType="error"
                showIcon
              />
            )}
          </div>
        </div>
      ) : (
        hvSubstation?.code
      ),
    },

    {
      label: t("address.address"),
      value: editingHvSubstation ? editableSubstationAddress : staticAddress,
    },
    {
      label: t("common.latitude"),
      value: editingHvSubstation ? coordinateInputTemplateWithValidation("latitude", -90, 90) : hvSubstation?.latitude,
    },
    {
      label: t("common.longitude"),
      value: editingHvSubstation
        ? coordinateInputTemplateWithValidation("longitude", -180, 180)
        : hvSubstation?.longitude,
    },
    {
      label: altRegionNames.data?.companyAltName ?? t("common.company"),
      // If the user does not have admin or customer level permissions but do have company or site level
      // permissions, make the company required and add validation to the drop down to ensure
      // they select a value, as they can only create resources at company/site level.
      required: editingHvSubstation && !userCanEditHvSubstationAtCustomerLevel,
      value: editingHvSubstation ? (
        <div>
          <div>
            <Controller
              control={control}
              name="company"
              defaultValue={companyName}
              rules={
                !userCanEditHvSubstationAtCustomerLevel
                  ? {
                      required: t<string>("common.pleaseSelect", { val: "$t(common.company)" }),
                      validate: {
                        notEmpty: (value) =>
                          (!!value && value !== "") || t<string>("common.pleaseSelect", { val: "$t(common.company)" }),
                      },
                    }
                  : undefined
              }
              render={() => (
                <CompanyDropdown
                  customerId={hvSubstation.customerId}
                  selectedCompanyName={companyName}
                  onCompanyChange={handleCompanyChange}
                  showClear
                />
              )}
            />
          </div>
          <div>
            {!userCanEditHvSubstationAtCustomerLevel && errors?.company && (
              <ValidationMessage
                invalidMessage={
                  (errors.company?.message as string) ?? t("common.pleaseSelect", { val: "$t(common.company)" })
                }
                validationType="error"
                showIcon
              />
            )}
          </div>
        </div>
      ) : (
        hvSubstation.company
      ),
    },

    // Site
    {
      label: altRegionNames.data?.siteAltName ?? t("common.site"),
      // If the user does not have admin or customer level permissions but do have company or site level
      // permissions, make the site required and add validation to the drop down to ensure
      // they select a value, as they can only create resources at company/site level.
      required: editingHvSubstation && !!companyName && !userCanEditHvSubstationAtCustomerLevel,
      value: editingHvSubstation ? (
        companyName ? (
          <div>
            <div>
              <Controller
                control={control}
                name="site"
                defaultValue={siteName}
                rules={
                  !userCanEditHvSubstationAtCustomerLevel
                    ? {
                        required: t<string>("common.pleaseSelect", { val: "$t(common.site)" }),
                        validate: {
                          notEmpty: (value) =>
                            (!!value && value !== "") || t<string>("common.pleaseSelect", { val: "$t(common.site)" }),
                        },
                      }
                    : undefined
                }
                render={() => (
                  <SiteDropdown
                    companyId={companyId}
                    customerId={hvSubstation.customerId}
                    selectedSiteName={siteName}
                    onSiteChange={handleSiteChange}
                  />
                )}
              />
            </div>
            <div>
              {!userCanEditHvSubstationAtCustomerLevel && errors?.site && (
                <ValidationMessage
                  invalidMessage={
                    (errors.site?.message as string) ??
                    t("substations.siteRequired", {
                      siteAltName: altRegionNames.data?.siteAltName ?? "common.site",
                      sitesAltName: altRegionNames.data?.sitesAltName ?? "common.sites",
                    })
                  }
                  validationType="error"
                  showIcon
                />
              )}
            </div>
          </div>
        ) : (
          <SiteDropdown
            companyId={companyId}
            customerId={hvSubstation.customerId}
            selectedSiteName={siteName}
            onSiteChange={handleSiteChange}
          />
        )
      ) : (
        hvSubstation.site
      ),
    },
    {
      label: t("substations.accessNotes"),
      value: editingHvSubstation ? (
        hvSubstation.accessNotes ? (
          <div>
            <div>
              <Controller
                control={control}
                name="site"
                defaultValue={hvSubstation.accessNotes}
                rules={{
                  required: false,
                  maxLength: 500,
                  validate: {
                    isChanged: (value) => value !== hvSubstation.accessNotes,
                  },
                }}
                render={() => (
                  <InputTextarea
                    value={hvSubstation.accessNotes}
                    className={errors.accessNotes ? "p-invalid" : undefined}
                    id={"accessNotes"}
                    onChange={handleAccessNotesChange}
                    name={"accessNotes"}
                  />
                )}
              />
            </div>
          </div>
        ) : (
          <InputTextarea
            value={hvSubstation.accessNotes}
            maxLength={500}
            className={errors.accessNotes ? "p-invalid" : undefined}
            id={"accessNotes"}
            onChange={handleAccessNotesChange}
            name={"accessNotes"}
          />
        )
      ) : (
        hvSubstation.accessNotes
      ),
    },
  ];

  function handleSaveUpdates() {
    trigger().then((isValid) => {
      if (hvSubstation && userCanEditHvSubstation && isValid) {
        const substationDetails: Partial<HvSubstation> = {
          id: hvSubstation.id,
          name: hvSubstation.name,
          code: hvSubstation.code,
          address: address,
          latitude: hvSubstation.latitude || 0, //defaulting lat/long values to 0 if empty instead of making the field required - task #11760
          longitude: hvSubstation.longitude || 0,
          company: companyName,
          site: siteName,
          accessNotes: hvSubstation.accessNotes,
        };

        const substationUpdateSubmission: HvSubstationSubmission = {
          hvSubstation: substationDetails,
        };
        updateHvSubstation(substationUpdateSubmission);
      }
    });
  }

  function handleCancelUpdate() {
    setEditingHvSubstation(false);
    // Reset form
    reset();
    // Undo any changes that the user has not saved
    setHvSubstation(retrievedAsset?.data && convertAssetToHvSubstation(retrievedAsset.data));
    setAddress(retrievedAddress.data);
    setCompanyName(retrievedAsset.data?.company);
    setSiteName(retrievedAsset.data?.site);
  }

  const handleErrorSnackbarClose = () => {
    setShowErrorSnackbar(false);
  };

  if (retrievedAsset.error) {
    return (
      <div className="w-100 d-flex justify-content-center pt-2">
        <MessageToast
          text={t("common.dataFetchError", {
            val: "$t(substations.hvSubstation)",
          })}
          mode={ToastMode.Error}
          show
        />
      </div>
    );
  } else if (userCanViewSubstation === false) {
    return (
      <div className="w-100 d-flex justify-content-center pt-2">
        <MessageCard
          messageType={MessageType.Warning}
          title={t("common.operationForbiddenError", {
            operation: t("common.view"),
            item: t("substations.hvSubstation"),
          })}
          secondaryText={t("common.requestAccess")}
        />
      </div>
    );
  } else {
    return (
      <React.Fragment>
        <Container fluid>
          <Row>
            <Col className="pb-3">
              <RouteBreadcrumbs
                customPageTitle={
                  retrievedAsset.isLoading
                    ? t("substations.hvSubstation")
                    : `${t("substations.hvSubstation")} - ${retrievedAsset?.data?.name || ""}`
                }
              />
            </Col>
            <Col xs="auto">
              {userCanDeleteHvSubstation && !editingHvSubstation && (
                <DeleteConfirmationButton
                  confirmationDialogHeader={t("common.deleteConfirmationShort", {
                    val: t("substations.substation"),
                    transform: "lowercase",
                  })}
                  confirmationDialogMessage={t("common.deleteConfirmation", {
                    val: t("substations.substation"),
                    transform: "lowercase",
                  })}
                  onConfirmDeleteButtonClick={deleteHvSubstation}
                >
                  {t("common.deleteEntity", {
                    entity: t("substations.substation"),
                  })}
                </DeleteConfirmationButton>
              )}
            </Col>
          </Row>

          {hvSubstationDelete.isError && affectedWorkOrderIds?.length > 0 && (
            <Row className="my-3">
              <MessageCard
                title={t("substations.unableToDeleteSubstationError")}
                content={t("substations.substationInUseError", {
                  val: t("workorders.activeWorkorder", {
                    count: affectedWorkOrderIds.length,
                  }),
                })}
                secondaryText={t("substations.substationRemoveReferenceError", {
                  action: t("workorders.completeOrCancel"),
                  item: t("workorders.workorder", {
                    count: affectedWorkOrderIds.length,
                  }),
                })}
                cardActions={
                  <Box display="flex" flexDirection="column">
                    {affectedWorkOrderIds.map((workorderId) => (
                      <Link
                        key={`link-${workorderId}`}
                        to={workorderViewerPath?.replace(":workorderId", workorderId.toString() || "") || ""}
                      >
                        {workorderId}
                      </Link>
                    ))}
                  </Box>
                }
                messageType={MessageType.Error}
              />
            </Row>
          )}

          {hvSubstation && (
            <Row>
              <Col lg={6}>
                <Card
                  title={t("substations.substationDetails")}
                  className="mb-3"
                  loading={pageLoading}
                  action={
                    userCanEditHvSubstation && (
                      <>
                        <Box display="flex" justifyContent="flex-end">
                          {editingHvSubstation ? (
                            <>
                              <ProgressButton
                                startIcon={<SaveIcon />}
                                onClick={handleSaveUpdates}
                                disabled={updateHvSubstationLoading}
                                loading={updateHvSubstationLoading}
                              >
                                {" "}
                                {t("common.save")}{" "}
                              </ProgressButton>
                              <CancelButton onClick={handleCancelUpdate} className="m1-1" />
                            </>
                          ) : (
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<EditIcon />}
                              onClick={() => setEditingHvSubstation(true)}
                            >
                              {t("common.edit")}{" "}
                            </Button>
                          )}
                        </Box>
                      </>
                    )
                  }
                >
                  <FormContainer dataList={hvSubstationDetailsList} />
                </Card>
              </Col>

              <Col lg={6}>
                <Card title={t("asset.assets")} className="mb-3">
                  {hvSubstation.id && (
                    <UpdatableSwitchgearDetails
                      substationId={hvSubstation.id}
                      editable={userCanEditHvSubstation}
                      customerId={hvSubstation.customerId}
                    />
                  )}
                </Card>
              </Col>
            </Row>
          )}
        </Container>

        <Snackbar
          open={updateHvSubstationError}
          onClose={() => {
            resetUpdateHvSubstation();
          }}
        >
          <div>
            <MessageToast
              text={t("common.dataUpdateError", {
                val: "$t(substations.hvSubstation)",
              })}
              mode={ToastMode.Error}
              show={true}
            />
          </div>
        </Snackbar>

        <Snackbar
          open={showSnackbar}
          onClose={() => {
            setShowSnackbar(false);
          }}
          message={snackbarText}
        />

        <Snackbar open={showErrorSnackbar} onClose={handleErrorSnackbarClose}>
          <Alert severity="error" variant="filled">
            {errorSnackbarText}
          </Alert>
        </Snackbar>
      </React.Fragment>
    );
  }
}
